import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import GoogleReviewsWidget from '../GoogleReviewsWidget';
import { REFERENCES } from './offerData';
import type { OfferReference } from './types';

const ReferenceCard: React.FC<{ reference: OfferReference }> = ({ reference }) => (
  <article className="flex h-full flex-col rounded-2xl border border-line-strong bg-white/5 p-5 md:p-6">
    <div className="flex items-center gap-4 mb-4">
      {reference.logo && (
        <div className="h-16 w-16 flex-shrink-0 rounded-xl bg-white/10 border border-line-strong p-2.5 flex items-center justify-center">
          <img src={reference.logo} alt={`Logo ${reference.name}`} className="h-full w-full object-contain" />
        </div>
      )}
      <div className="min-w-0">
        <h3 className="text-fg font-semibold leading-tight">{reference.name}</h3>
        <p className="text-xs uppercase tracking-wider text-accent-soft mt-1">{reference.category}</p>
      </div>
    </div>

    {reference.screenshot && (
      <img
        src={reference.screenshot}
        alt={`Capture du site internet de ${reference.name}, réalisé par SOS IT Services`}
        loading="lazy"
        className="mb-4 w-full aspect-[16/10] rounded-lg border border-line object-cover object-top"
      />
    )}

    <p className="text-fg-muted text-sm leading-relaxed mb-4">{reference.description}</p>

    {reference.testimonial && (
      <blockquote className="mb-4 border-l-2 border-accent pl-3 text-sm text-fg">
        <p className="italic">{`«\u00a0${reference.testimonial.quote}\u00a0»`}</p>
        <footer className="mt-1 text-xs text-fg-muted">
          {reference.testimonial.author}, {reference.testimonial.role}
        </footer>
      </blockquote>
    )}

    <a
      href={reference.href}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-auto inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-accent-soft hover:text-fg transition-colors"
    >
      Voir le site <ExternalLink className="h-4 w-4" aria-hidden="true" />
    </a>
  </article>
);

const OfferReferences: React.FC = () => (
  <>
    <section className="section-spacing bg-canvas border-t border-line">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/35 bg-accent-strong/10 px-4 py-1.5 text-xs font-semibold tracking-wider text-accent-soft uppercase mb-4">
            Références
          </span>
          <h2 className="heading-section text-balance mb-2">
            Ils nous ont fait <span className="text-accent-soft">confiance</span>
          </h2>
          <p className="text-fg-muted text-sm">Des projets livrés avec succès</p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
        >
          {REFERENCES.map((reference) => (
            <ReferenceCard key={reference.name} reference={reference} />
          ))}
        </motion.div>
      </div>
    </section>
    <GoogleReviewsWidget />
  </>
);

export default OfferReferences;
